export const enPublishedApps = {
  title: "Apps",
  subtitle: "Access your analytics tools and insights",
  empty: "No published apps yet.",
  errors: {
    loadFailed: "Could not load apps. Please try again.",
  },
  search: {
    placeholder: "Search apps...",
    ariaLabel: "Search apps",
    empty: "No apps match your search.",
  },
  favorites: {
    title: "Favorites",
  },
  favorite: {
    addAriaLabel: "Add to favorites",
    removeAriaLabel: "Remove from favorites",
  },
  section: {
    toggleAriaLabel: "Toggle {{title}} section",
  },
  list: {
    ariaLabel: "Published apps",
    unknownCategory: "Uncategorized",
  },
  view: {
    header: "{{name}}",
    backAria: "Back to Apps",
    loadingAria: "Loading app",
    notFound: "App not found.",
    errors: {
      loadFailed: "Could not load the app. Please try again.",
    },
    snapshot: {
      selectAria: "Choose which data to view",
      default: "Default",
    },
    items: {
      loadingAria: "Loading charts, tables, and KPI cards",
      streamingAria: "Loading more charts, tables, and KPI cards",
      empty: "This app has no charts, tables, or KPI cards yet.",
      emptyTab: "This tab has no charts, tables, or KPI cards yet.",
      noData: "No rows returned.",
      itemSummaryLabel: "AI summary",
      appSummaryLabel: "AI Overview",
      tabSummaryLabel: "Tab overview",
      filterCascadeFailed: "Could not update the filters that depend on this one.",
      // Screen readers only — the visible placeholder is an animated skeleton, so there is no
      // on-screen string to translate.
      summaryGeneratingAria: "Generating AI summary",
      drilldown: {
        openAria: "Show details for {{name}}",
        close: "Close details",
        filteredDialogTitle: "{{name}} · {{filter}}",
        // Stands in for a filter column the clicked row left empty; see appStudio's twin.
        emptyValue: "(empty)",
        loadFailed: "Could not load these details. Please try again.",
        // Tooltip on the approve action while the dialog is showing rows it filtered locally and
        // the server's answer has not landed. Says what is happening rather than "please wait":
        // the rows are readable, it is only approving them that has to hold.
        confirming: "Confirming these rows…",
      },
      approval: {
        approve: "Approve selected ({{count}})",
        approveEmpty: "Approve selected",
        approving: "Approving…",
        nothingToApprove: "Nothing to approve — every row has been approved.",
        selectAllAria: "Select all rows",
        selectRowAria: "Select row {{index}}",
        approvedNotice: "{{count}} row(s) approved.",
        alreadyApprovedNotice:
          "{{count}} of the selected row(s) had already been approved.",
        approveFailed: "Could not approve these rows. Please try again.",
        taxonomy: {
          loadFailed:
            "Could not load the classification list. Rows can still be approved unchanged.",
          tooLarge:
            "That classification list is too large to edit here. Rows can still be approved unchanged.",
          cellAria: "{{column}} for row {{index}}",
          cellBlocked: "Pick level {{level}} first",
          cellIncomplete: "Pick a value",
          addValue: 'Add "{{value}}"',
          addValueFailed:
            "Could not save that new value for everyone. It is still applied to this row.",
          rowBlockedTooltip: "Pick level 1 before approving this row",
          // Tooltip and accessible name for the control beside an edited row's checkbox. Says the
          // action rather than the state, because the control *is* the state: it appears only on a
          // row carrying an unapproved edit, and clicking it puts that row back as it arrived.
          rowDiscardTooltip: "Discard this row's changes",
          pendingDraftsNotice: "{{count}} row(s) have unapproved changes.",
        },
        // The approve action in the drill-down dialog's header. It acts on the row that was
        // clicked in the table behind the dialog, not on whatever the dialog is displaying, so it
        // is singular where the grid's own button counts a selection.
        drilldown: {
          approve: "Approve",
          // A drill-down row the card has no row of its own to submit for. Deliberately says only
          // that: the usual cause is a target row this widget's query does not return, but the
          // same tooltip covers a row whose counterpart simply was not handed down.
          rowUnmatched: "This row can't be approved here",
        },
      },
      export: {
        triggerAria: "Export this table to CSV",
        triggerTooltip: "Export to CSV",
        title: "Export to CSV",
        closeAria: "Close export dialog",
        // The count is the whole result, not the page on screen, which is why it repeats the
        // number already in the card header rather than the rows currently visible.
        scope_one: "{{count}} row will be exported.",
        scope_other: "{{count}} rows will be exported.",
        columns: "Columns to include",
        selectAll: "Select all",
        // Marks a column the table does not display. It is still a real query column and can be
        // exported; the badge is there so nobody wonders why it is not on screen.
        hiddenColumn: "Hidden",
        nothingSelected: "Pick at least one column to export.",
        progress_one: "Exported {{count}} row...",
        progress_other: "Exported {{count}} rows...",
        failed: "Could not export this table. Please try again.",
        cancel: "Cancel",
        confirm: "Export",
        exporting: "Exporting",
        // Used when the widget's own title sanitizes away to nothing.
        fallbackName: "table",
      },
      errors: {
        loadFailed:
          "Could not load charts, tables, and KPI cards. Please try again.",
      },
    },
  },
} as const;
